/**
 * Multi-Vendor Cart Delivery Logic
 * Handles persistent location and vendor-specific delivery calculations
 */

class MultiVendorCartManager {
  constructor() {
    this.userLocation = null;
    this.vendorDeliveryData = {};
    this.cartVendors = new Set();
    this.ungroupedVendors = new Set();
    this.ungroupedVendorItems = new Map();
    this.warehouseDataReady = false;

    // Default fallback configuration used when real-time data is unavailable
    this.defaultDeliveryConfig = {
      baseTime: 30,
      perKmTime: 2.5,
      maxTime: 120,
      minTime: 30,
      baseCharge: 50,
      perKmCharge: 6
    };

    this.manualWarehouseOverrides = {
      'aldeno': 'Kormangala Wearhouse',
      'journey east clothing': 'Kormangala Wearhouse',
      'journey east': 'Kormangala Wearhouse',
      'little surprise box': 'Kormangala Wearhouse',
      'ore ofe': 'Kormangala Wearhouse',
      'shop serve': 'Kormangala Wearhouse',
      'sullitt': 'Kormangala Wearhouse',
      'supervek': 'Kormangala Wearhouse',
      'house of koala': 'Kormangala Wearhouse',
      'shop mauve': 'Kormangala Wearhouse',
      'funny tales': 'Kormangala Wearhouse',
      'studio ojs': 'Kormangala Wearhouse',
      'just audlting': 'Kormangala Wearhouse',
      'diam beauty': 'Kormangala Wearhouse',
      'walawali': 'Kormangala Wearhouse',
      'hustle clothing': 'Kormangala Wearhouse',
      'pinacolada': 'Campus Sutra',
      'french accent': 'Campus Sutra',
      'instafab plus': 'Campus Sutra',
      'qissa': 'Campus Sutra',
      'sohi': 'Campus Sutra',
      'haute sauce': 'Campus Sutra',
      'united colors of benetton': 'Scott International',
      'all weather gear': 'Scott International',
      'lords & kings': 'Lords and Kings',
      'street junkies': 'Street Junkies',
      'cop underdog': 'Cop underdog',
      'adro': 'Adro',
      'drippinhot': 'Drippinhot',
      'mustard': 'Mustard',
      'the bear house': 'The Bear House',
      'street 9': 'Street 9',
      'isu': 'ISU',
      'chumbak': 'Chumbak'
    };

    this.hiddenVendorDisplayName = 'Exclusive Partner';
    this.hiddenVendorNames = new Set([
      'cop underdog',
      'street junkies',
      'lords & kings',
      'lords and kings'
    ]);

    this.handleQuantityInput = this.handleQuantityInput.bind(this);
    
    this.init();
  }

  init() {
    // Load saved location
    this.loadUserLocation();
    
    // Listen for cart updates
    this.setupCartListeners();
    
    // Listen for location updates
    this.setupLocationListeners();

    // Listen for warehouse data availability
    this.setupWarehouseDataListeners();
    
    // Initialize cart analysis
    this.analyzeCartVendors();
  }

  loadUserLocation() {
    try {
      const savedLocation = localStorage.getItem('userLocation');
      if (savedLocation) {
        this.userLocation = JSON.parse(savedLocation);
        // Update header display
        this.updateHeaderLocationDisplay();
      }
    } catch (error) {
      console.error('Error loading user location:', error);
    }
  }

  setupCartListeners() {
    // Listen for cart updates
    document.addEventListener('cart:updated', (event) => {
      this.analyzeCartVendors();
      this.updateCartDisplay();
    });

    // Listen for cart section updates
    const cartObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.target.id === 'main-cart-items') {
          setTimeout(() => {
            this.analyzeCartVendors();
            this.updateCartDisplay();
          }, 100);
        }
      });
    });

    const cartSection = document.getElementById('main-cart-items');
    if (cartSection) {
      cartObserver.observe(cartSection, { childList: true, subtree: true });
    }

    document.addEventListener('input', this.handleQuantityInput, true);
    document.addEventListener('change', this.handleQuantityInput, true);
  }

  setupLocationListeners() {
    // Listen for location updates from the enhanced modal
    document.addEventListener('locationUpdated', (event) => {
      this.userLocation = this.mergeLocationDetail(event.detail);
      this.calculateWarehouseDeliveryData();
      this.updateCartDisplay();
      this.updateHeaderLocationDisplay();
    });
  }

  setupWarehouseDataListeners() {
    this.handleWarehouseDataReady = this.handleWarehouseDataReady.bind(this);
    document.addEventListener('warehouseDeliveryReady', this.handleWarehouseDataReady);
    this.tryHydrateWarehouseData();
  }

  handleWarehouseDataReady() {
    this.warehouseDataReady = true;
    this.analyzeCartVendors();
    this.updateCartDisplay();
  }

  tryHydrateWarehouseData() {
    const deliveryInstance = window.warehouseDelivery;
    if (!deliveryInstance) {
      return;
    }

    const hasLocations = deliveryInstance.vendorLocations && Object.keys(deliveryInstance.vendorLocations).length > 0;
    if (hasLocations) {
      this.warehouseDataReady = true;
      this.analyzeCartVendors();
      this.updateCartDisplay();
    }
  }

  // Get warehouse location for a vendor/brand
  getWarehouseForVendor(vendor) {
    if (window.shopifyDeliveryAPI && window.shopifyDeliveryAPI.getWarehouseForVendor) {
      const result = window.shopifyDeliveryAPI.getWarehouseForVendor(vendor);
      return result;
    }
    
    return null;
  }

  getCartItemsContainer() {
    const isCartPage = window.location.pathname.includes('/cart');
    if (isCartPage) {
      const mainCart = document.getElementById('main-cart-items');
      return mainCart ? mainCart.closest('.cart-products-section') : null;
    }
    const cartDrawer = document.querySelector('cart-drawer');
    return cartDrawer || document.body;
  }

  analyzeCartVendors() {
    this.cartVendors.clear();
    this.ungroupedVendors.clear();
    this.ungroupedVendorItems.clear();
    this.warehouseGroups = {};

    const container = this.getCartItemsContainer();
    const cartItems = container ? container.querySelectorAll('.cart-item') : document.querySelectorAll('.cart-item');

    cartItems.forEach((item, index) => {
      const vendor = this.extractVendorFromItem(item);

      if (vendor && vendor !== 'Unknown' && vendor !== '' && vendor !== 'undefined') {
        this.cartVendors.add(vendor);

        const groupingInfo = this.resolveGroupingForVendor(vendor);

        if (groupingInfo.warehouse) {
          this.addItemToWarehouseGroup(groupingInfo.warehouse, vendor, item, groupingInfo.location);
        } else {
          this.addItemToUngroupedVendor(vendor, item);
        }

        item.setAttribute('data-vendor', vendor);
      }
    });

    this.logWarehouseGroupingSummary();

    this.renderDeliveryShipments();
    this.calculateWarehouseDeliveryData();
  }

  logWarehouseGroupingSummary() {
    // Summary logging removed for production
  }

  extractVendorFromItem(item) {
    if (!item) return null;

    if (item.hasAttribute('data-vendor')) {
      const vendor = item.getAttribute('data-vendor')?.trim();
      if (vendor) return vendor;
    }

    const selectors = [
      '.caption-with-letter-spacing',
      '.vendor-label',
      'p.caption-with-letter-spacing',
      'td.cart-item__details p:first-child'
    ];

    for (const selector of selectors) {
      const vendorElement = item.querySelector(selector);
      if (vendorElement && vendorElement.textContent.trim()) {
        return vendorElement.textContent.trim();
      }
    }

    const detailsCell = item.querySelector('td.cart-item__details');
    if (detailsCell) {
      const firstP = detailsCell.querySelector('p');
      if (firstP && firstP.textContent.trim()) {
        return firstP.textContent.trim();
      }
    }

    return null;
  }

  getManualWarehouseOverride(vendor) {
    const key = (vendor || '').toString().trim().toLowerCase();
    if (!key) return null;
    return this.manualWarehouseOverrides?.[key] || null;
  }

  isHiddenVendor(vendor) {
    const key = (vendor || '').toString().trim().toLowerCase();
    if (!key) return false;
    return this.hiddenVendorNames?.has(key) || false;
  }

  getDisplayVendorName(vendor) {
    if (!vendor) return '';
    return this.isHiddenVendor(vendor) ? this.hiddenVendorDisplayName : vendor;
  }

  replaceVendorText(element, vendor, displayName) {
    if (!element) return;
    const text = element.textContent;
    if (!text) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    if (trimmed.toLowerCase() === vendor.toLowerCase()) {
      element.textContent = displayName;
      element.classList.add('vendor-name-hidden');
    }
  }

  applyHiddenVendorMaskToItem(item, vendor) {
    if (!this.isHiddenVendor(vendor) || !item) {
      return;
    }

    const displayName = this.getDisplayVendorName(vendor);
    const selectors = [
      '.cart-item__vendor',
      '.vendor-label',
      '.caption-with-letter-spacing',
      '.vendor-name'
    ];

    selectors.forEach((selector) => {
      const elements = item.querySelectorAll(selector);
      elements.forEach((el) => this.replaceVendorText(el, vendor, displayName));
    });
  }

  isQuantityInput(element) {
    if (!element) {
      return false;
    }

    if (element.classList?.contains('quantity__input')) {
      return true;
    }

    const nameAttr = element.getAttribute?.('name');
    return nameAttr === 'updates[]';
  }

  handleQuantityInput(event) {
    if (!event || !this.isQuantityInput(event.target)) {
      return;
    }

    window.requestAnimationFrame(() => {
      this.updateVendorSummaryQuantities();
    });
  }

  getQuantityFromRow(row) {
    if (!row || typeof row.querySelector !== 'function') {
      return 0;
    }

    const qtyInput = row.querySelector('.quantity__input');
    if (qtyInput) {
      const directValue = parseInt(qtyInput.value, 10);
      if (Number.isFinite(directValue) && directValue > 0) {
        return directValue;
      }

      const attrValue = parseInt(qtyInput.getAttribute('value'), 10);
      if (Number.isFinite(attrValue) && attrValue > 0) {
        return attrValue;
      }

      const dataValue = parseInt(qtyInput.dataset?.quantity, 10);
      if (Number.isFinite(dataValue) && dataValue > 0) {
        return dataValue;
      }
    }

    const rowDataQuantity = parseInt(row.dataset?.quantity, 10);
    if (Number.isFinite(rowDataQuantity) && rowDataQuantity > 0) {
      return rowDataQuantity;
    }

    const attrQuantity = parseInt(row.getAttribute?.('data-quantity'), 10);
    if (Number.isFinite(attrQuantity) && attrQuantity > 0) {
      return attrQuantity;
    }

    return 1;
  }

  getTotalQuantityForItems(items = []) {
    if (!items || !items.length) {
      return 0;
    }

    return items.reduce((sum, item) => {
      return sum + this.getQuantityFromRow(item);
    }, 0);
  }

  resolveGroupingForVendor(vendor) {
    const manualOverride = this.getManualWarehouseOverride(vendor);
    const rawWarehouse = this.getWarehouseForVendor(vendor) || manualOverride;
    const identifier = rawWarehouse || manualOverride || vendor;
    const resolvedWarehouse = this.getResolvedWarehouseData(identifier);

    let warehouse = (resolvedWarehouse.warehouseName || manualOverride || rawWarehouse || vendor || '').toString().trim();
    if (!warehouse && manualOverride) {
      warehouse = manualOverride;
    }

    let location = resolvedWarehouse.locationData || null;
    if (!location && manualOverride) {
      const manualResolved = this.getResolvedWarehouseData(manualOverride);
      location = manualResolved.locationData || null;
    }

    return {
      vendor,
      rawWarehouse,
      warehouse,
      location
    };
  }

  addItemToWarehouseGroup(warehouse, vendor, item, location = null) {
    if (!this.warehouseGroups[warehouse]) {
      this.warehouseGroups[warehouse] = {
        vendors: new Set(),
        items: [],
        location: location || null
      };
    }

    this.warehouseGroups[warehouse].vendors.add(vendor);
    this.warehouseGroups[warehouse].items.push(item);
    this.warehouseGroups[warehouse].location = this.warehouseGroups[warehouse].location || location || null;

    this.applyHiddenVendorMaskToItem(item, vendor);

    // Vendor grouped under warehouse
  }

  addItemToUngroupedVendor(vendor, item) {
    this.ungroupedVendors.add(vendor);
    if (!this.ungroupedVendorItems.has(vendor)) {
      this.ungroupedVendorItems.set(vendor, []);
    }
    this.ungroupedVendorItems.get(vendor).push(item);
    this.applyHiddenVendorMaskToItem(item, vendor);
    // No warehouse found, adding to ungrouped vendors
  }

  calculateWarehouseDeliveryData() {
    if (!this.userLocation) {
      this.setDefaultDeliveryData();
      return;
    }

    const warehouseNames = Object.keys(this.warehouseGroups || {});

    const warehousePromises = warehouseNames.map(async (warehouse) => {
      const warehouseGroup = this.warehouseGroups[warehouse];
      const vendors = Array.from(warehouseGroup.vendors);

      vendors.forEach((vendor) => this.setVendorLoadingState(vendor, true));

      try {
        const deliveryDetails = await this.calculateRealDeliveryDetails(warehouse);

        vendors.forEach((vendor) => {
          const enrichedDetails = {
            ...deliveryDetails,
            warehouse,
            vendors,
            isWarehouseGroup: true
          };

          this.vendorDeliveryData[vendor] = enrichedDetails;
          this.setVendorLoadingState(vendor, false);
          this.updateVendorDeliveryDisplay(vendor, enrichedDetails);
          this.updateWarehouseHeaderDisplay(warehouse, enrichedDetails);
          this.notifyVendorDeliveryUpdate(vendor, enrichedDetails);
        });
      } catch (error) {
        console.error(`Error calculating delivery for warehouse ${warehouse}:`, error);
        vendors.forEach((vendor) => {
          this.setVendorLoadingState(vendor, false);
          const fallbackDetails = this.calculateDefaultDeliveryDetails(vendor, this.getResolvedWarehouseData(warehouse));
          this.vendorDeliveryData[vendor] = fallbackDetails;
          this.updateVendorDeliveryDisplay(vendor, fallbackDetails);
          this.updateWarehouseHeaderDisplay(warehouse, fallbackDetails);
          this.notifyVendorDeliveryUpdate(vendor, fallbackDetails);
        });
      }
    });

    const ungroupedPromises = Array.from(this.ungroupedVendors || []).map(async (vendor) => {
      this.setVendorLoadingState(vendor, true);

      try {
        const deliveryDetails = await this.calculateRealDeliveryDetails(vendor);
        const enrichedDetails = {
          ...deliveryDetails,
          warehouse: deliveryDetails.warehouse || vendor,
          isWarehouseGroup: false
        };

        this.vendorDeliveryData[vendor] = enrichedDetails;
        this.setVendorLoadingState(vendor, false);
        this.updateVendorDeliveryDisplay(vendor, enrichedDetails);
        this.updateWarehouseHeaderDisplay(enrichedDetails.warehouse, enrichedDetails);
        this.notifyVendorDeliveryUpdate(vendor, enrichedDetails);
      } catch (error) {
        console.error(`Error calculating delivery for ungrouped vendor ${vendor}:`, error);
        const fallbackDetails = this.calculateDefaultDeliveryDetails(vendor, this.getResolvedWarehouseData(vendor));
        this.vendorDeliveryData[vendor] = fallbackDetails;
        this.setVendorLoadingState(vendor, false);
        this.updateVendorDeliveryDisplay(vendor, fallbackDetails);
        this.updateWarehouseHeaderDisplay(fallbackDetails.warehouse, fallbackDetails);
        this.notifyVendorDeliveryUpdate(vendor, fallbackDetails);
      }
    });

    Promise.all([...warehousePromises, ...ungroupedPromises])
      .then(() => {
      })
      .catch((error) => {
        console.error('Error resolving warehouse delivery data:', error);
      });
  }

  async calculateRealDeliveryDetails(vendor) {
    const resolvedData = this.getResolvedWarehouseData(vendor);
    const { warehouseName, locationData } = resolvedData;

    try {
      const hasCoordinates = Boolean(
        this.userLocation?.coordinates?.latitude && this.userLocation?.coordinates?.longitude
      );

      // Use Shopify Delivery API if available and we have coordinates
      if (window.shopifyDeliveryAPI && hasCoordinates) {
        const customerLocation = {
          latitude: this.userLocation.coordinates.latitude,
          longitude: this.userLocation.coordinates.longitude
        };

        const deliveryDetails = await window.shopifyDeliveryAPI.calculateDeliveryDetails(vendor, customerLocation);

        if (deliveryDetails && deliveryDetails.available) {
          const normalizedTime = this.normalizeDeliveryTime(deliveryDetails.deliveryTime);
          const calculatedCharge = typeof deliveryDetails.deliveryCharge === 'number'
            ? Math.round(deliveryDetails.deliveryCharge)
            : this.calculateFallbackDeliveryCharge(deliveryDetails.distance, deliveryDetails.vendorLocation || locationData);

          return {
            deliveryTime: normalizedTime,
            deliveryCharge: calculatedCharge,
            distance: deliveryDetails.distance,
            vendorLocation: deliveryDetails.vendorLocation || locationData || null,
            warehouse: warehouseName,
            isRealTime: true
          };
        }
      }

      // Fallback to default calculation
      return this.calculateDefaultDeliveryDetails(vendor, resolvedData);

    } catch (error) {
      console.error(`Error in real delivery calculation for ${vendor}:`, error);
      return this.calculateDefaultDeliveryDetails(vendor, resolvedData);
    }
  }

  calculateDefaultDeliveryDetails(vendor, resolvedData = null) {
    const { warehouseName, locationData } = resolvedData || this.getResolvedWarehouseData(vendor);

    const params = locationData?.deliveryParams || this.defaultDeliveryConfig;
    const distance = this.calculateDistanceForVendor(locationData);

    const baseTime = params.baseTime ?? this.defaultDeliveryConfig.baseTime;
    const perKmTime = params.perKmTime ?? this.defaultDeliveryConfig.perKmTime;
    const maxTime = params.maxTime ?? this.defaultDeliveryConfig.maxTime;
    const minTime = params.minTime ?? this.defaultDeliveryConfig.minTime;

    let calculatedTime = baseTime + (distance * perKmTime);
    calculatedTime = Math.max(minTime, Math.min(maxTime, calculatedTime));

    const deliveryCharge = this.calculateFallbackDeliveryCharge(distance, locationData);

    return {
      deliveryTime: Math.round(calculatedTime),
      deliveryCharge: Math.round(deliveryCharge),
      distance,
      vendorLocation: locationData ? {
        name: locationData.name,
        address: locationData.address,
        latitude: locationData.latitude,
        longitude: locationData.longitude
      } : null,
      warehouse: warehouseName,
      isRealTime: false
    };
  }

  mergeLocationDetail(detail) {
    if (!detail) {
      return this.userLocation;
    }

    const location = {
      ...(detail.location || {})
    };

    if (detail.deliveryTime && !location.deliveryTime) {
      location.deliveryTime = detail.deliveryTime;
    }

    if (typeof detail.distance === 'number' && Number.isFinite(detail.distance)) {
      location.distance = detail.distance;
    }

    if (!location.coordinates && this.userLocation?.coordinates) {
      location.coordinates = this.userLocation.coordinates;
    }

    return location;
  }

  getResolvedWarehouseData(identifier) {
    const rawValue = (identifier || '').toString().trim();
    const deliveryInstance = window.warehouseDelivery || {};
    const vendorLocations = deliveryInstance.vendorLocations || {};
    const brandMap = deliveryInstance.brandToWarehouseMap || {};
    const canonicalMap = deliveryInstance.canonicalWarehouseMap || {};
    const manualMap = this.manualWarehouseOverrides || {};

    const canonicalLookup = (value) => {
      if (value === null || value === undefined) return null;
      const key = value.toString().trim();
      if (!key) return null;
      return (
        canonicalMap[key] ||
        canonicalMap[key.toLowerCase?.()] ||
        canonicalMap[key.toUpperCase?.()] ||
        manualMap[key.toLowerCase?.()] ||
        null
      );
    };

    const resolveFromBrandMap = (value) => {
      if (!value) return null;
      if (brandMap[value]) {
        return brandMap[value];
      }

      const lowerValue = value.toLowerCase();
      if (manualMap[lowerValue]) {
        return manualMap[lowerValue];
      }

      const insensitiveMatch = Object.entries(brandMap).find(([brand]) => brand.toLowerCase() === lowerValue);
      if (insensitiveMatch) {
        return insensitiveMatch[1];
      }

      return null;
    };

    let warehouseName = canonicalLookup(rawValue) || rawValue;
    let locationData = vendorLocations[warehouseName] || vendorLocations[rawValue] || null;

    if (vendorLocations[rawValue]) {
      const canonical = canonicalLookup(rawValue);
      warehouseName = canonical || rawValue;
      locationData = vendorLocations[warehouseName] || vendorLocations[rawValue];
    } else {
      const mappedWarehouse = resolveFromBrandMap(rawValue);

      if (mappedWarehouse) {
        const canonicalTarget = canonicalLookup(mappedWarehouse) || mappedWarehouse;
        warehouseName = canonicalTarget;
        locationData = vendorLocations[canonicalTarget] || vendorLocations[mappedWarehouse] || null;
      }

      if (!locationData) {
        const lowerId = rawValue.toLowerCase();

        const exactMatch = Object.entries(vendorLocations).find(([name]) => name.toLowerCase() === lowerId);
        if (exactMatch) {
          warehouseName = canonicalLookup(exactMatch[0]) || exactMatch[0];
          locationData = vendorLocations[warehouseName] || exactMatch[1];
        } else {
          const partialMatch = Object.entries(vendorLocations).find(([name]) => {
            const lowerName = name.toLowerCase();
            return lowerName.includes(lowerId) || lowerId.includes(lowerName);
          });

          if (partialMatch) {
            warehouseName = canonicalLookup(partialMatch[0]) || partialMatch[0];
            locationData = vendorLocations[warehouseName] || partialMatch[1] || null;
          }
        }
      }
    }

    const canonicalFinal = canonicalLookup(warehouseName);
    if (warehouseName && canonicalFinal) {
      warehouseName = canonicalFinal;
      locationData = vendorLocations[warehouseName] || locationData;
    }

    warehouseName = warehouseName ? warehouseName.toString().trim() : warehouseName;

    return { warehouseName, locationData };
  }

  getWarehouseSlug(name) {
    return (name || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  calculateDistanceForVendor(locationData) {
    if (!this.userLocation) {
      return 0;
    }

    const deliveryInstance = window.warehouseDelivery;
    const hasCoordinates = Boolean(
      locationData?.latitude &&
      locationData?.longitude &&
      this.userLocation?.coordinates?.latitude &&
      this.userLocation?.coordinates?.longitude
    );

    if (deliveryInstance && typeof deliveryInstance.calculateDistance === 'function' && hasCoordinates) {
      try {
        return deliveryInstance.calculateDistance(
          locationData.latitude,
          locationData.longitude,
          this.userLocation.coordinates.latitude,
          this.userLocation.coordinates.longitude
        );
      } catch (error) {
        // Error calculating distance
      }
    }

    if (typeof this.userLocation.distance === 'number' && Number.isFinite(this.userLocation.distance)) {
      return this.userLocation.distance;
    }

    return 0;
  }

  calculateFallbackDeliveryCharge(distance, locationData) {
    const deliveryInstance = window.warehouseDelivery;

    if (deliveryInstance && typeof deliveryInstance.calculateDeliveryCharge === 'function' && locationData) {
      try {
        return deliveryInstance.calculateDeliveryCharge(distance, locationData);
      } catch (error) {
        // Error calculating delivery charge
      }
    }

    let charge = this.defaultDeliveryConfig.baseCharge;

    if (distance <= 5) {
      charge = 30;
    } else if (distance <= 10) {
      charge = 50;
    } else if (distance <= 20) {
      charge = 80;
    } else if (distance > 0) {
      charge = 120;
    }

    const adjustmentMap = {
      'Campus Sutra': 0,
      'Drippinhot': 10,
      'The Bear House': 20,
      'Lords and Kings': 15
    };

    if (locationData?.name && typeof adjustmentMap[locationData.name] !== 'undefined') {
      charge += adjustmentMap[locationData.name];
    }

    return charge;
  }

  normalizeDeliveryTime(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();

      const hourMinuteMatch = trimmed.match(/(\d+(?:\.\d+)?)\s*h(?:ours?)?\s*(\d+)\s*m/i);
      if (hourMinuteMatch) {
        return Math.round(parseFloat(hourMinuteMatch[1]) * 60 + parseInt(hourMinuteMatch[2], 10));
      }

      const hourOnlyMatch = trimmed.match(/(\d+(?:\.\d+)?)\s*h/i);
      if (hourOnlyMatch) {
        return Math.round(parseFloat(hourOnlyMatch[1]) * 60);
      }

      const rangeMatch = trimmed.match(/(\d+)\s*[-–]\s*(\d+)/);
      if (rangeMatch) {
        const min = parseInt(rangeMatch[1], 10);
        const max = parseInt(rangeMatch[2], 10);
        return Math.round((min + max) / 2);
      }

      const minuteMatch = trimmed.match(/(\d+)/);
      if (minuteMatch) {
        return parseInt(minuteMatch[1], 10);
      }
    }

    return this.defaultDeliveryConfig.baseTime;
  }

  formatDeliveryTime(value) {
    const minutes = this.normalizeDeliveryTime(value);

    if (window.warehouseDelivery && typeof window.warehouseDelivery.formatDeliveryTime === 'function') {
      try {
        return window.warehouseDelivery.formatDeliveryTime(minutes);
      } catch (error) {
        // Error formatting delivery time
      }
    }

    if (minutes < 60) {
      return `${minutes} mins`;
    }

    if (minutes === 60) {
      return '1 hour';
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (remainingMinutes === 0) {
      return hours === 1 ? '1 hour' : `${hours} hours`;
    }

    return `${hours}h ${remainingMinutes}m`;
  }

  /**
   * Second line under ETA: short copy only (no long "Vendor A, Vendor B" lists).
   * One shop: "2 items · Shop Mauve". Several: "2 items · 3 shops".
   */
  buildDeliveryGroupSubtitle(itemCount, displayVendorNames) {
    const n = Math.max(0, Number(itemCount) || 0);
    const itemsLabel = `${n} item${n !== 1 ? 's' : ''}`;
    const names = Array.from(new Set((displayVendorNames || []).filter(Boolean)));
    if (!names.length) {
      return itemsLabel;
    }
    if (names.length === 1) {
      return `${itemsLabel} · ${names[0]}`;
    }
    return `${itemsLabel} · ${names.length} shops`;
  }

  notifyVendorDeliveryUpdate(vendor, deliveryDetails) {
    document.dispatchEvent(new CustomEvent('vendorDeliveryUpdated', {
      detail: {
        vendor,
        deliveryDetails
      }
    }));
  }

  updateWarehouseHeaderDisplay(warehouse, deliveryDetails) {
    if (!warehouse) {
      return;
    }

    const slug = this.getWarehouseSlug(warehouse);
    const header = document.querySelector(`.warehouse-header[data-warehouse="${slug}"]`);

    if (!header) {
      return;
    }

    const timeElement = header.querySelector('.warehouse-header__delivery .delivery-time');
    const iconElement = header.querySelector('.warehouse-header__delivery .delivery-icon');

    if (iconElement) {
      iconElement.textContent = '⚡';
    }

    if (timeElement) {
      timeElement.textContent = `Delivery in ${this.formatDeliveryTime(deliveryDetails.deliveryTime)}`;
      if (deliveryDetails.isRealTime) {
        timeElement.classList.add('real-time');
      } else {
        timeElement.classList.remove('real-time');
      }
    }

    this.updateShipmentCard(slug, deliveryDetails);
  }

  getWarehouseSummary() {
    const summary = {};

    Object.keys(this.warehouseGroups || {}).forEach((warehouse) => {
      const vendors = Array.from(this.warehouseGroups[warehouse].vendors || []);
      const referenceVendor = vendors[0];

      summary[warehouse] = {
        warehouse,
        vendors,
        deliveryData: this.vendorDeliveryData[referenceVendor] || null,
        items: this.warehouseGroups[warehouse].items || [],
        location: this.warehouseGroups[warehouse].location || null,
        isWarehouseGroup: true
      };
    });

    (this.ungroupedVendors || new Set()).forEach((vendor) => {
      summary[vendor] = {
        warehouse: vendor,
        vendors: [vendor],
        deliveryData: this.vendorDeliveryData[vendor] || null,
        items: this.ungroupedVendorItems.get(vendor) || [],
        location: null,
        isWarehouseGroup: false
      };
    });

    return summary;
  }

  renderDeliveryShipments() {
    const container = this.getCartItemsContainer();
    const table = container ? container.querySelector('.cart-items') : document.querySelector('.cart-items');
    if (!table) return;

    const columnCount = table.querySelectorAll('thead th').length || 5;

    const shipments = [];

    Object.keys(this.warehouseGroups || {}).forEach((warehouse) => {
      const group = this.warehouseGroups[warehouse];
      const vendors = Array.from(group.vendors || []);
      const rows = Array.from(group.items || []);
      const deliveryData = this.vendorDeliveryData[vendors[0]] || null;
      const label = group.location?.name || warehouse;
      const displayVendors = vendors.map((vendorName) => this.getDisplayVendorName(vendorName));

      shipments.push({
        type: 'warehouse',
        key: this.getShipmentKey('warehouse', warehouse),
        label,
        vendors,
        displayVendors,
        rows,
        deliveryData,
        location: group.location || (deliveryData?.vendorLocation || null)
      });
    });

    (this.ungroupedVendors || new Set()).forEach((vendor) => {
      const rows = Array.from(this.ungroupedVendorItems.get(vendor) || []);
      const deliveryData = this.vendorDeliveryData[vendor] || null;
      const resolved = this.getResolvedWarehouseData(vendor);
      const displayName = this.getDisplayVendorName(vendor);
      const label = resolved.locationData?.name || displayName;
      const displayVendors = [displayName];

      shipments.push({
        type: 'vendor',
        key: this.getShipmentKey('vendor', vendor),
        label,
        vendors: [vendor],
        displayVendors,
        rows,
        deliveryData,
        location: resolved.locationData || (deliveryData?.vendorLocation || null)
      });
    });

    // Remove existing table bodies before rebuilding groups
    table.querySelectorAll('tbody').forEach((body) => body.remove());

    if (!shipments.length) {
      const fallbackBody = document.createElement('tbody');
      table.appendChild(fallbackBody);
      table.classList.remove('cart-items--grouped');
      return;
    }

    table.classList.add('cart-items--grouped');

    shipments.forEach((shipment) => {
      const groupBody = document.createElement('tbody');
      groupBody.className = 'delivery-group';
      groupBody.dataset.shipment = shipment.key;

      const headerRow = document.createElement('tr');
      headerRow.className = 'delivery-group__header';
      const headerCell = document.createElement('td');
      headerCell.colSpan = columnCount;

      const itemCount = Math.max(this.getTotalQuantityForItems(shipment.rows), shipment.rows.length);

      const deliveryTime = shipment.deliveryData
        ? this.formatDeliveryTime(shipment.deliveryData.deliveryTime)
        : 'Calculating…';

      const displayVendorNames = Array.from(new Set((shipment.displayVendors || []).filter(Boolean)));
      const subtitle =
        displayVendorNames.length > 0
          ? this.buildDeliveryGroupSubtitle(itemCount, displayVendorNames)
          : this.buildDeliveryGroupSubtitle(itemCount, [this.hiddenVendorDisplayName]);

      headerCell.innerHTML = `
        <div class="delivery-group__summary">
          <span class="delivery-group__icon">⚡</span>
          <div class="delivery-group__meta">
            <div class="delivery-group__eta">Delivery in ${deliveryTime}</div>
            <div class="delivery-group__vendors">${subtitle}</div>
          </div>
        </div>
      `;

      headerRow.appendChild(headerCell);
      groupBody.appendChild(headerRow);

      shipment.rows.forEach((row) => {
        groupBody.appendChild(row);
      });

      table.appendChild(groupBody);
    });

    this.refreshHiddenVendorDisplays();
    this.updateVendorSummaryQuantities();
  }

  refreshHiddenVendorDisplays() {
    (this.cartVendors || new Set()).forEach((vendor) => {
      if (!this.isHiddenVendor(vendor)) {
        return;
      }

      const displayName = this.getDisplayVendorName(vendor);
      const vendorElements = document.querySelectorAll(`[data-vendor="${vendor}"]`);
      vendorElements.forEach((element) => {
        const selectors = [
          '.vendor-name',
          '.vendor-label',
          '.cart-item__vendor',
          '.caption-with-letter-spacing'
        ];

        selectors.forEach((selector) => {
          element.querySelectorAll(selector).forEach((el) => this.replaceVendorText(el, vendor, displayName));
        });
      });
    });
  }

  updateVendorSummaryQuantities() {
    const vendorsArray = Array.from(this.cartVendors || []);

    vendorsArray.forEach((vendor) => {
      const cartRows = Array.from(document.querySelectorAll(`.cart-item[data-vendor="${vendor}"]`));
      const totalQuantity = Math.max(this.getTotalQuantityForItems(cartRows), cartRows.length);
      const label = `${totalQuantity} item${totalQuantity !== 1 ? 's' : ''}`;

      document
        .querySelectorAll(`.vendor-section-summary[data-vendor="${vendor}"] .vendor-item-count`)
        .forEach((element) => {
          element.textContent = label;
        });
    });

    Object.entries(this.warehouseGroups || {}).forEach(([warehouse, group]) => {
      const rows = Array.from(group.items || []);
      const shipmentKey = this.getShipmentKey('warehouse', warehouse);
      this.updateShipmentHeaderQuantityByKey(shipmentKey, rows, Array.from(group.vendors || []));
    });

    (this.ungroupedVendors || new Set()).forEach((vendor) => {
      const rows = Array.from(this.ungroupedVendorItems.get(vendor) || []);
      const shipmentKey = this.getShipmentKey('vendor', vendor);
      this.updateShipmentHeaderQuantityByKey(shipmentKey, rows, [vendor]);
    });
  }

  updateShipmentHeaderQuantityByKey(shipmentKey, rows, vendors = []) {
    if (!shipmentKey) {
      return;
    }

    const groupElement = document.querySelector(`.delivery-group[data-shipment="${shipmentKey}"]`);
    if (!groupElement) {
      return;
    }

    const summaryElement = groupElement.querySelector('.delivery-group__vendors');
    if (!summaryElement) {
      return;
    }

    const totalQuantity = Math.max(this.getTotalQuantityForItems(rows), rows.length);
    const displayVendors = vendors
      .map((vendor) => this.getDisplayVendorName(vendor))
      .filter(Boolean);
    const subtitle =
      displayVendors.length > 0
        ? this.buildDeliveryGroupSubtitle(totalQuantity, displayVendors)
        : this.buildDeliveryGroupSubtitle(totalQuantity, [this.hiddenVendorDisplayName]);

    summaryElement.textContent = subtitle;
  }

  updateShipmentCard(shipmentKey, deliveryDetails) {
    if (!shipmentKey) return;
    const group = document.querySelector(`.delivery-group[data-shipment="${shipmentKey}"]`);
    if (!group) return;

    const eta = group.querySelector('.delivery-group__eta');
    if (eta) {
      eta.textContent = `Delivery in ${this.formatDeliveryTime(deliveryDetails.deliveryTime)}`;
      eta.classList.toggle('real-time', Boolean(deliveryDetails.isRealTime));
    }

    const icon = group.querySelector('.delivery-group__icon');
    if (icon) {
      icon.classList.toggle('real-time', Boolean(deliveryDetails.isRealTime));
    }

    group.classList.toggle('real-time', Boolean(deliveryDetails.isRealTime));
  }

  getShipmentKey(type, name) {
    if (!name) return null;
    const base = type === 'warehouse' ? name : `vendor-${name}`;
    return this.getWarehouseSlug(base);
  }

  setVendorLoadingState(vendor, isLoading) {
    const vendorElements = document.querySelectorAll(`[data-vendor="${vendor}"]`);
    vendorElements.forEach(element => {
      if (isLoading) {
        element.classList.add('delivery-loading', 'vendor-loading');
      } else {
        element.classList.remove('delivery-loading', 'vendor-loading');
      }
    });
  }

  updateVendorDeliveryDisplay(vendor, deliveryDetails) {
    const formattedTime = this.formatDeliveryTime(deliveryDetails.deliveryTime);

    // Update vendor header delivery info
    const vendorHeaders = document.querySelectorAll(`[data-vendor="${vendor}"] .vendor-delivery-info`);
    vendorHeaders.forEach((header) => {
      const timeElement = header.querySelector('.delivery-time');

      if (timeElement) {
        const withinWarehouseHeader = timeElement.closest('.warehouse-header__delivery');
        timeElement.textContent = withinWarehouseHeader
          ? `Delivery in ${formattedTime}`
          : `⚡ Delivery in ${formattedTime}`;
        timeElement.classList.toggle('real-time', Boolean(deliveryDetails.isRealTime));
      }

      const chargeElement = header.querySelector('.delivery-charge');
      if (chargeElement) {
        chargeElement.remove();
      }
    });

    const vendorSummaryTotals = document.querySelectorAll(`.vendor-section-summary[data-vendor="${vendor}"] .vendor-delivery-total`);
    vendorSummaryTotals.forEach((total) => {
      total.textContent = `⚡ Delivery in ${formattedTime}`;
      total.classList.toggle('real-time', Boolean(deliveryDetails.isRealTime));
    });

    // Remove inline badges if any existed previously
    document
      .querySelectorAll(`.cart-item[data-vendor="${vendor}"] .cart-item__delivery-badge`)
      .forEach((badge) => badge.remove());

    const shipmentKey = deliveryDetails.isWarehouseGroup
      ? this.getShipmentKey('warehouse', deliveryDetails.warehouse)
      : this.getShipmentKey('vendor', vendor);
    this.updateShipmentCard(shipmentKey, deliveryDetails);
  }

  setDefaultDeliveryData() {
    this.cartVendors.forEach((vendor) => {
      this.setDefaultVendorDeliveryData(vendor);
    });
  }

  setDefaultVendorDeliveryData(vendor) {
    const defaultData = this.calculateDefaultDeliveryDetails(vendor);
    this.vendorDeliveryData[vendor] = defaultData;
    this.updateVendorDeliveryDisplay(vendor, defaultData);
    this.updateWarehouseHeaderDisplay(defaultData.warehouse, defaultData);
    this.notifyVendorDeliveryUpdate(vendor, defaultData);
  }

  updateCartDisplay() {
    const cartItemsContainer = document.querySelector('.cart-items tbody');
    if (!cartItemsContainer) return;

    const warehouseCount = Object.keys(this.warehouseGroups || {}).length;
    const ungroupedCount = this.ungroupedVendors?.size || 0;
    const deliveryGroupCount = warehouseCount + ungroupedCount;
    const hasMultipleDeliveries = deliveryGroupCount > 1;
    
    this.renderDeliveryShipments();

    if (hasMultipleDeliveries) {
      this.addMultiWarehouseNotice();
    } else {
      this.removeMultiWarehouseNotice();
    }
  }

  addMultiWarehouseNotice() {
    // Remove existing notice if any
    this.removeMultiWarehouseNotice();

    const cartProductsSection = document.querySelector('.cart-products-section');
    if (!cartProductsSection) return;

    const notice = document.createElement('div');
    notice.className = 'multi-warehouse-notice';
    notice.innerHTML = `
      <div class="multi-warehouse-notice__content">
        <div class="multi-warehouse-notice__icon">⚡</div>
        <div class="multi-warehouse-notice__text">
          <strong>Your Deliveries Are On Their Way</strong>
          <p>We’ve planned the best arrival slots for you. Track each upcoming delivery in the timeline below.</p>
        </div>
      </div>
    `;

    // Insert at the top of cart products section
    cartProductsSection.insertBefore(notice, cartProductsSection.firstChild);
  }

  removeMultiWarehouseNotice() {
    const existingNotice = document.querySelector('.multi-warehouse-notice');
    if (existingNotice) {
      existingNotice.remove();
    }
  }

  addMultiVendorNotice() {
    // Remove existing notice if any
    this.removeMultiVendorNotice();

    const cartProductsSection = document.querySelector('.cart-products-section');
    if (!cartProductsSection) return;

    const notice = document.createElement('div');
    notice.className = 'multi-vendor-notice';
    notice.innerHTML = `
      <div class="multi-vendor-notice__content">
        <div class="multi-vendor-notice__icon">⚡</div>
        <div class="multi-vendor-notice__text">
          <strong>Multiple Vendors Detected</strong>
          <p>Your order contains items from different vendors. These will be delivered separately with individual arrival times.</p>
        </div>
      </div>
    `;

    // Insert at the top of cart products section
    cartProductsSection.insertBefore(notice, cartProductsSection.firstChild);
  }

  removeMultiVendorNotice() {
    const existingNotice = document.querySelector('.multi-vendor-notice');
    if (existingNotice) {
      existingNotice.remove();
    }
  }

  groupItemsByVendor() {
    const cartItemsContainer = document.querySelector('.cart-items tbody');
    if (!cartItemsContainer) return;

    // Get all cart items
    const cartItems = Array.from(cartItemsContainer.querySelectorAll('.cart-item'));
    
    // Group items by vendor
    const vendorGroups = {};
    cartItems.forEach(item => {
      const vendor = item.getAttribute('data-vendor') || 'Unknown';
      if (!vendorGroups[vendor]) {
        vendorGroups[vendor] = [];
      }
      vendorGroups[vendor].push(item);
    });

    // Clear container
    cartItemsContainer.innerHTML = '';

    // Add vendor sections
    Object.keys(vendorGroups).forEach(vendor => {
      this.addVendorSection(cartItemsContainer, vendor, vendorGroups[vendor]);
    });
  }

  addVendorSection(container, vendor, items) {
    const deliveryData = this.vendorDeliveryData[vendor];
    
    // Create vendor header
    const vendorHeader = document.createElement('tr');
    vendorHeader.className = 'vendor-section-header';
    vendorHeader.dataset.vendor = vendor;
    vendorHeader.innerHTML = `
      <td colspan="5" class="vendor-header-cell">
        <div class="vendor-header-content">
          <div class="vendor-info">
            <h3 class="vendor-name">${this.getDisplayVendorName(vendor)}</h3>
            <div class="vendor-delivery-info">
              <span class="delivery-time">⚡ Delivery in ${deliveryData ? this.formatDeliveryTime(deliveryData.deliveryTime) : 'Calculating…'}</span>
            </div>
          </div>
        </div>
      </td>
    `;

    container.appendChild(vendorHeader);

    // Add items for this vendor
    items.forEach(item => {
      container.appendChild(item);
      this.applyHiddenVendorMaskToItem(item, vendor);
    });

    // Add vendor summary
    const vendorSummary = document.createElement('tr');
    vendorSummary.className = 'vendor-section-summary';
    vendorSummary.dataset.vendor = vendor;
    const totalQuantity = Math.max(this.getTotalQuantityForItems(items), items.length);
    vendorSummary.innerHTML = `
      <td colspan="5" class="vendor-summary-cell">
        <div class="vendor-summary-content">
          <div class="vendor-totals">
            <span class="vendor-item-count">${totalQuantity} item${totalQuantity !== 1 ? 's' : ''}</span>
            <span class="vendor-delivery-total">⚡ ${deliveryData ? this.formatDeliveryTime(deliveryData.deliveryTime) : 'Calculating…'}</span>
          </div>
        </div>
      </td>
    `;

    container.appendChild(vendorSummary);
  }

  removeVendorGrouping() {
    // Remove vendor headers and summaries
    const vendorHeaders = document.querySelectorAll('.vendor-section-header, .vendor-section-summary');
    vendorHeaders.forEach(header => header.remove());
  }

  updateHeaderLocationDisplay() {
    if (!this.userLocation) return;
    const resolvedCity = String(
      this.userLocation.cityName || this.userLocation.city || ''
    ).trim();
    if (!resolvedCity) return;

    const locationText = document.querySelector('.modern-header__location-text');
    if (locationText) {
      // Show delivery time based on warehouse groups
      const warehouseCount = Object.keys(this.warehouseGroups || {}).length;
      
      if (warehouseCount > 1) {
        const avgDeliveryTime = this.calculateAverageWarehouseDeliveryTime();
        locationText.textContent = `Delivery in ${this.formatDeliveryTime(avgDeliveryTime)} (multiple deliveries)`;
        locationText.classList.add('multi-vendor');
      } else if (warehouseCount === 1) {
        const warehouse = Object.keys(this.warehouseGroups)[0];
        const vendors = Array.from(this.warehouseGroups[warehouse].vendors);
        const deliveryData = this.vendorDeliveryData[vendors[0]];
        if (deliveryData) {
          locationText.textContent = `Delivery in ${this.formatDeliveryTime(deliveryData.deliveryTime)}`;
          locationText.classList.remove('multi-vendor');
        }
      } else if (this.cartVendors.size > 0) {
        // Fallback to vendor-based display
        const vendor = Array.from(this.cartVendors)[0];
        const deliveryData = this.vendorDeliveryData[vendor];
        if (deliveryData) {
          locationText.textContent = `Delivery in ${this.formatDeliveryTime(deliveryData.deliveryTime)}`;
          locationText.classList.remove('multi-vendor');
        }
      } else {
        locationText.textContent = this.userLocation.deliveryTime || 'Delivery in 30-90 min';
        locationText.classList.remove('multi-vendor');
      }
    }
  }

  calculateAverageWarehouseDeliveryTime() {
    const warehouses = Object.keys(this.warehouseGroups || {});
    if (warehouses.length === 0) return 60;

    let totalTime = 0;
    warehouses.forEach(warehouse => {
      const vendors = Array.from(this.warehouseGroups[warehouse].vendors);
      const deliveryData = this.vendorDeliveryData[vendors[0]];
      if (deliveryData) {
        totalTime += this.normalizeDeliveryTime(deliveryData.deliveryTime);
      }
    });

    return Math.round(totalTime / warehouses.length);
  }

  // Public API methods
  getVendorDeliveryData(vendor) {
    return this.vendorDeliveryData[vendor];
  }

  getAllVendorDeliveryData() {
    return this.vendorDeliveryData;
  }

  getCartVendors() {
    return Array.from(this.cartVendors);
  }

  hasMultipleVendors() {
    return this.cartVendors.size > 1;
  }

  getTotalDeliveryCharges() {
    let total = 0;
    const processedWarehouses = new Set();
    
    // Calculate charges per warehouse (not per vendor)
    Object.keys(this.warehouseGroups || {}).forEach(warehouse => {
      if (!processedWarehouses.has(warehouse)) {
        const vendors = Array.from(this.warehouseGroups[warehouse].vendors);
        const deliveryData = this.vendorDeliveryData[vendors[0]];
        if (deliveryData) {
          total += deliveryData.deliveryCharge;
          processedWarehouses.add(warehouse);
        }
      }
    });
    
    // Add charges for ungrouped vendors
    this.cartVendors.forEach(vendor => {
      const deliveryData = this.vendorDeliveryData[vendor];
      if (deliveryData && !deliveryData.isWarehouseGroup) {
        total += deliveryData.deliveryCharge;
      }
    });
    
    return total;
  }
}

// Enhanced Location Manager for Multi-Vendor Support
class EnhancedLocationManager {
  constructor() {
    this.multiVendorCart = null;
    this.init();
  }

  init() {
    // Initialize multi-vendor cart manager
    this.multiVendorCart = new MultiVendorCartManager();
    
    // Override the existing location update method
    this.overrideLocationUpdate();
  }

  overrideLocationUpdate() {
    // Override the existing location update function
    const originalUpdateHeaderLocation = window.updateHeaderLocation;
    
    window.updateHeaderLocation = (location, deliveryTime, weatherData = null, distance = null) => {
      // Call original function if it exists
      if (originalUpdateHeaderLocation) {
        originalUpdateHeaderLocation(location, deliveryTime, weatherData, distance);
      }
      
      // Update multi-vendor cart with new location
      if (this.multiVendorCart) {
        this.multiVendorCart.userLocation = this.multiVendorCart.mergeLocationDetail({
          location,
          deliveryTime,
          distance
        });
        this.multiVendorCart.calculateWarehouseDeliveryData();
        this.multiVendorCart.updateCartDisplay();
        this.multiVendorCart.updateHeaderLocationDisplay();
      }
    };
  }

  // Public API
  getMultiVendorCart() {
    return this.multiVendorCart;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Initialize enhanced location manager
  window.enhancedLocationManager = new EnhancedLocationManager();
  
  // Make multi-vendor cart manager globally accessible
  window.multiVendorCart = window.enhancedLocationManager.getMultiVendorCart();
  
  // Initialize location display on all pages
  initializeLocationDisplay();
  
  // Initialize cart analysis on all pages (not just cart page)
  initializeCartAnalysis();
});

// Initialize location display on all pages
function initializeLocationDisplay() {
  if (typeof window.skippRefreshHeaderLocationPills === 'function') {
    window.skippRefreshHeaderLocationPills();
    return;
  }
  const savedLocation = localStorage.getItem('userLocation');
  if (!savedLocation) return;
  try {
    const locationData = JSON.parse(savedLocation);
    const city = String(locationData.cityName || locationData.city || '').trim();
    const locationDisplay = document.querySelector('.modern-header__location-text');
    if (!locationDisplay || !city || !locationData.deliveryTime) return;
    let dt = String(locationData.deliveryTime).trim();
    while (/^delivery\s+in\s+/i.test(dt)) {
      dt = dt.replace(/^delivery\s+in\s+/i, '').trim();
    }
    locationDisplay.textContent = `Delivery in ${dt}`;
  } catch (e) {
    console.error('Error loading saved location globally:', e);
  }
}

// Initialize cart analysis on all pages
function initializeCartAnalysis() {
  // Check if we're on cart page
  const isCartPage = window.location.pathname.includes('/cart') || document.querySelector('.cart-items');
  
  if (isCartPage && window.multiVendorCart) {
    setTimeout(() => {
      window.multiVendorCart.analyzeCartVendors();
      window.multiVendorCart.updateCartDisplay();
    }, 500);
  }
  
  // Also listen for cart updates globally
  document.addEventListener('cart:updated', () => {
    if (window.multiVendorCart) {
      setTimeout(() => {
        window.multiVendorCart.analyzeCartVendors();
        window.multiVendorCart.updateCartDisplay();
      }, 100);
    }
  });
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { MultiVendorCartManager, EnhancedLocationManager };
}
